"use client";

import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck, FileCheck, FileSpreadsheet, Layers, Ruler } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface CapabilityItem {
  titleEn: string;
  titleZh: string;
  detailEn: string;
  detailZh: string;
}
const capabilities: CapabilityItem[] = [{
  titleEn: "Custom Radii & Flat Profiles",
  titleZh: "定制曲率半径与平面规格",
  detailEn: "Precision curved arcs, vibrating sieve panels, and tensioned flat screens engineered to exact frame geometries.",
  detailZh: "高精度弧形弯曲筛板、振动筛分板及张紧式平板筛网，严密匹配设备机架几何结构。"
}, {
  titleEn: "Multilayer & Sintered Laminates",
  titleZh: "多层烧结与复合层压结构",
  detailEn: "Diffusion-bonded woven mesh packs, perforated plate reinforcement, and heavy-duty wear-resistant backings.",
  detailZh: "扩散焊接烧结编织复合网板、冲孔保护内衬及重载高耐磨背衬结构。"
}, {
  titleEn: "ASTM Tolerances & Certifications",
  titleZh: "严格遵循 ASTM 与材质认证",
  detailEn: "Full ASTM E2016 compliance, tight dimensional tolerances, and EN 10204 3.1 material test certification.",
  detailZh: "严格符合 ASTM E2016 规范与公差控制，提供完整的 EN 10204 3.1 材质检验证书。"
}];
export default function CustomFabricationCta() {
  const [lang, setLang] = useState<"en" | "zh">(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("mf_lang");
      return saved === "zh" || saved === "en" ? saved : "en";
    }
    return "en";
  });
  const shouldReduceMotion = useReducedMotion();
  useEffect(() => {
    try {
      const savedLang = localStorage.getItem("mf_lang");
      if (savedLang === "zh" || savedLang === "en") {
        setLang(savedLang);
      }
    } catch {
      // Ignore localStorage errors
    }
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "en" | "zh";
      }>;
      if (customEvent.detail?.lang) {
        setLang(customEvent.detail.lang);
      } else {
        const current = localStorage.getItem("mf_lang");
        if (current === "zh" || current === "en") {
          setLang(current);
        }
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "mf_lang" && (e.newValue === "zh" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("mf-language-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("mf-language-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const isZh = lang === "zh";
  const content = {
    badge: isZh ? "精密规格与定制织造" : "Precision specifications & custom weaves",
    heading: isZh ? "根据您的严苛丝网技术规格精密制造" : "Engineered to your exact mesh specifications",
    body: isZh ? "从定制分条网带和多层烧结复合网，到冲压滤圆片及圆筒形筛网，我们严格遵循 ASTM 标准，以严苛公差制造各类精密过滤组件。" : "From custom slit coils and multilayer sintered packs to stamped filter discs and cylindrical screens, we manufacture components to tight tolerances in compliance with strict ASTM standards.",
    quoteBtn: isZh ? "索取定制报价" : "Request a custom quote",
    consultBtn: isZh ? "咨询技术工程师" : "Consult with an engineer",
    compliance: isZh ? "符合 ASTM E2016 标准 • 24小时内提供材质测试证书及 CAD 审核支持" : "ASTM E2016 Compliant • Material test certificates & CAD review within 24 hours"
  };
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="customfabricationcta" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-20 md:py-28 lg:py-32">
      {/* Subtle metallurgical mesh grid background decoration */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.08]" aria-hidden="true">
        <svg className="h-full w-full stroke-current" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="custom-fabrication-pattern" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M0 16h32M16 0v32" strokeWidth="1" />
              <circle cx="16" cy="16" r="1.5" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#custom-fabrication-pattern)" />
        </svg>
      </div>

      {/* Radial soft industrial highlight glow */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-[40rem] rounded-lg bg-white/10 blur-3xl" aria-hidden="true" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mx-auto max-w-4xl text-center" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="inline-flex items-center justify-center mb-6">
            <Badge variant="outline" className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground font-mono text-xs tracking-wider uppercase px-3 py-1.5 rounded-lg">
              <Layers className="size-3.5 mr-1 text-primary-foreground" />
              {content.badge}
            </Badge>
          </motion.div>

          {/* Heading */}
          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-primary-foreground leading-tight mb-6">
            {content.heading}
          </motion.h2>

          {/* Body */}
          <motion.p variants={itemVariants} className="text-base sm:text-lg text-primary-foreground/85 font-default max-w-2xl mx-auto mb-10 leading-relaxed">
            {content.body}
          </motion.p>

          {/* Technical capabilities feature cards */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left mb-10">
            {capabilities.map((item, i) => <div key={item.titleEn} data-index={i} className="rounded-lg border border-border/40 bg-card text-card-foreground p-5 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-accent hover:shadow-md">
                <div className="flex items-center gap-2 mb-2 text-foreground font-default font-semibold text-sm">
                  {i === 0 && <Ruler className="size-4 text-primary shrink-0" />}
                  {i === 1 && <Layers className="size-4 text-primary shrink-0" />}
                  {i === 2 && <FileSpreadsheet className="size-4 text-primary shrink-0" />}
                  <span>{isZh ? item.titleZh : item.titleEn}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-normal">
                  {isZh ? item.detailZh : item.detailEn}
                </p>
              </div>)}
          </motion.div>

          {/* Action buttons */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <Button asChild size="lg" className="w-full sm:w-auto bg-card text-foreground font-semibold shadow-md hover:bg-card/90 rounded-md">
              <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                <span>{content.quoteBtn}</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground rounded-md">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <FileCheck className="size-4 text-primary-foreground/80" />
                <span>{content.consultBtn}</span>
              </Link>
            </Button>
          </motion.div>

          {/* Assurance note */}
          <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center gap-2 text-xs text-primary-foreground/75">
            <CircleCheck className="size-3.5 text-accent-foreground shrink-0" />
            <span>{content.compliance}</span>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}